import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';


export interface AwardItem {
  name: string;
  desc: string;
  image: { url: string; alt: string; width: number; height: number };
}

interface AwardsListProps {
  items: AwardItem[];
}

const AwardsList = ({ items }: AwardsListProps) => {
  const [activeIndex, setActiveIndex] = useState(0); // always one active
  const [currentIndex, setCurrentIndex] = useState<number | null>(null); // for mobile
  const containerRef = useRef<HTMLDivElement | null>(null);

  useGSAP(() => {
    const mm = gsap.matchMedia();

    mm.add("(min-width: 1280px)", () => {
      if (!containerRef.current) return;

      const itemsEls = gsap.utils.toArray<HTMLElement>(".award-item");

      gsap.from(itemsEls, {
        opacity: 0,
        y: "2rem",
        duration: 0.3,
        stagger: 0.1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top 80%",
          end: "bottom 20%",
        },
      });
    });
  });

  // Ensure one is always active
  useEffect(() => {
    if (window.innerWidth >= 1440 && activeIndex === null) {
      setActiveIndex(0);
    }
  }, [activeIndex]);

  return (
    <>
      {/* DESKTOP */}
      <div ref={containerRef} className="hidden lg:flex flex-col">
        {items.map((item, index) => {
          const isActive = activeIndex === index;

          return (
            <div
              key={index}
              className={`award-item py-[1.2rem] container-grid group ease-out will-change-[padding] transition-[padding] duration-300 ${
                isActive ? "py-[2rem]" : "hover:py-[2rem]"
              }`}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => setActiveIndex(index)}
            >
              <h3
                className={`heading-4 ease-out will-change-[font-size] transition-[font-size,color] duration-300 col-span-5 text-end flex items-center justify-end ${
                  isActive
                    ? "text-dark-blue text-[2.4rem] xl:text-[3.2rem]"
                    : "text-dark-blue-40"
                }`}
              >
                {item.name}
              </h3>

              <div
                className={`col-span-1 flex items-center justify-center transition-opacity ease-out duration-500 ${
                  isActive ? "opacity-100" : "opacity-0"
                }`}
              >
                <div className="bg-dark-blue w-[8.2rem] h-[1px]" />
              </div>

              <div
                className={`col-span-3 xl:col-span-3 relative flex items-center transition-opacity  `}
              >
                <div className="absolute top-0">
                  <p className={`text-1 mb-[2.4rem] ${
                  isActive ? "opacity-100" : "opacity-0"
                }`}>{item.desc}</p>
                  <div
                    style={{
                      aspectRatio: item.image.width / item.image.height,
                      width: `${(item.image.width / item.image.height) * 100}%`,
                    }}
                    className={`max-w-full ease-out duration-500 relative ${
                  isActive ? "opacity-100" : "opacity-0"
                }`}
                  >
                    <Image
                      src={item.image.url}
                      alt={item.image.alt}
                      fill
                      sizes="400px"
                      className="w-auto object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* MOBILE */}
      <div className="lg:hidden">
        {items.map((item, index) => (
          <div
            key={index}
            onClick={() =>
              setCurrentIndex(currentIndex === index ? null : index)
            }
            className="py-[1.8rem] w-full border-dusty-blue-10 border-b-[1px]"
          >
            <div className="flex text-left justify-between items-center">
              <h3 className='heading-4'>{item.name}</h3>
              <span className="block p-[1rem]">
                <img
                  className={`${
                    index === currentIndex ? "-rotate-90" : "rotate-90"
                  } min-w-[1.2rem] transition-transform duration-500 min-h-[1.2rem]`}
                  fetchPriority="low"
                  src="/images/arrow_right.svg"
                  alt="Стрелка вправо"
                />
              </span>
            </div>

            <div
              className={`${
                index === currentIndex ? "max-h-[50rem]" : "max-h-0"
              } overflow-hidden block text-left transition-[max-height] duration-500 w-full`}
            >
              <div className="block pt-[1rem]">
                <p className="text-1 pr-[4rem] mb-[1.6rem]">{item.desc}</p>
                <span className="block w-full relative md:max-w-[23.6rem] aspect-[1.2/1]">
                  <Image
                    src={item.image.url}
                    alt={item.image.alt}
                    fill
                    sizes="500px"

                    className="w-auto object-cover"
                  />
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default AwardsList;
